import { Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Breadcrumb } from '@app/api/core/decorator/breadcrumb.decorator';
import { GsmEsignDialogComponent } from '@app/shared/components/dialog/gsm-esign-dialog/gsm-esign-dialog.component';
import { MainContentComponent } from '@app/shared/components/main-content/main-content.component';
import { OverviewComponent } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/components/overview/overview.component';
import { GsmErrorService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm-error.service';
import { GsmService } from '@app/shared/gsm/gsm-ui-lib-ng-shared/src/lib/gsm.service';
import { DataService } from '@app/shared/services/data/data.service';
import { PeziService } from '@app/shared/services/pezi.service';
import { RouterService } from '@app/shared/services/router.service';
import { ProcessDraftInfo } from '@app/shared/services/state/model/gsm/gsm.model';
import { Action, ReactiveDialogResult } from '@app/shared/services/ui/dialog/dialog.types';
import { Benutzer, Kunde, SchadenUebersicht } from '@gsm/entities';
import { PageVertriebscockpitComponent } from 'app/views/vertriebscockpit/page/page.vertriebscockpit.component';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { first, map, switchMap, take, tap } from 'rxjs/operators';

@Breadcrumb({
  name: 'Schadenübersicht'
})

@Component({
  selector:      'app-schadenuebersicht',
  templateUrl:   './schadenuebersicht.component.html',
  styleUrls:     ['./schadenuebersicht.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone:    true,
  imports:       [
    MainContentComponent,
    OverviewComponent
  ]
})
export class SchadenUebersichtComponent extends PageVertriebscockpitComponent implements OnInit {
  private dataService = inject(DataService);
  private routerService = inject(RouterService);
  private gsmService = inject(GsmService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private gsmErrorService = inject(GsmErrorService);

  private peziService = inject(PeziService);
  private destroyRef = inject(DestroyRef);

  private readonly TIME_LIMIT_DRAFT = 120 * 60 * 1000; // 120 minutes´

  private ORDER_BY_STATUS = {
    'gemeldet': 1,
    'angelegt': 2,
    'erledigt': 3
  };

  isARGEorKBMA = false;
  schadenUebersicht: SchadenUebersicht[] = [];
  entwurfUebersicht: SchadenUebersicht[] = [];
  personId: number;
  benutzer: Benutzer | null;
  isKbma = false;
  schadenUebersichtIsLoading: boolean;
  entwurfUebersichtIsLoading: boolean;
  processInstanceId: string;
  isDeleteDisabled = false;
  schwebebestand: SchadenUebersicht | null = null;
  highlightedSchadenNummer: string;

  currentUebersichtSub: Subscription;

  constructor() {
    super();
    this.llapi.patchValue('layout.pageLayout.breadcrumb', true);
  }

  ngOnInit() {
    this.llapi.patchValue('layout.spinner', false);

    // gather ui data: benutzer and route params
    forkJoin([
      this.route.params.pipe(
        first(),
        takeUntilDestroyed(this.destroyRef)
      ),
      // TODO: re-use existing GSM benutzer object from store
      this.dataService.getBenutzer()
        .pipe(
          switchMap(() => this.gsmService.getGsmBenutzer())
        )
    ])
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        // set instance properties
        tap(([params, benutzer]) => {
          const selectedPerson = this.llapi.getValue('layout.uiData.selectedPerson');
          if (selectedPerson || params?.kundenId || benutzer) {
            this.personId = +(params.kundenId || selectedPerson);
            this.benutzer = benutzer;
            this.isKbma = !!benutzer?.kbma;
          }
        })
      )
      .subscribe(() => this.updateOverview());

    this.processInstanceId = this.llapi.getValue('gsm.processInstanceId');

    // TODO consider moving processDraftInfo to a separate object (only processInstanceId & lastEditTimestamp are needed here)
    const processDraftInfo = this.llapi.getValue('gsm');
    this._checkDraftExpiry(processDraftInfo);

    this.route.paramMap.pipe(first())
      .subscribe(params => this.highlightedSchadenNummer = params.get('schadennummer') || null);
  }

  updateOverview() {
    if (this.currentUebersichtSub) {
      this.currentUebersichtSub.unsubscribe();
    }
    let schadenUebersicht$;
    let entwurfUebersicht$;
    const kunde = {kundeId: this.personId ? this.personId.toString() : null} as Kunde;
    this.schadenUebersichtIsLoading = true;
    this.entwurfUebersichtIsLoading = true;
    // We do not have a benutzer so we won't to anything
    if (!this.benutzer) {
      return;
    }
    // !! ATTENTION: next line is different in o30 !!
    //   --> const kunde = {kundeId: this.personId ? this.personId.toString() : null} as Kunde;

    // load Schäden of ARGE if Benutzer is not a KBMA
    if (this.isARGEorKBMA && !this.isKbma) {
      schadenUebersicht$ = this.gsmService.getBetreuerSchadenUebersichtList();
      entwurfUebersicht$ = this.gsmService.getBetreuerSchadenEntwurfList(
        this.benutzer.betreuerNummer.toString()
      );
      // load Schäden of Kundenbüro if Benutzer is a KBMA and toggles isARGEorKBMA
    } else if (this.isARGEorKBMA && this.isKbma) {
      schadenUebersicht$ = this.gsmService.getKundenbueroSchadenUebersichtList();
      entwurfUebersicht$ = this.gsmService.getKundenbueroSchadenEntwurfList(
        this.benutzer.kbNummer
      );
      // load Schäden of chosen Kunde
    } else if (kunde && kunde.kundeId && !this.isARGEorKBMA) {
      schadenUebersicht$ = this.gsmService.getKundeSchadenUebersichtList(
        kunde.kundeId
      );
      entwurfUebersicht$ = this.gsmService.getKundeSchadenEntwurfList(
        kunde.kundeId
      );
      // load Schäden of Benutzer
    } else if (!kunde.kundeId && !this.isARGEorKBMA) {
      schadenUebersicht$ = this.gsmService.getBenutzerSchadenUebersichtList();
      entwurfUebersicht$ = this.gsmService.getBenutzerSchadenEntwurfList(
        this.benutzer.reporterEmail
      );
    }

    this.currentUebersichtSub = schadenUebersicht$
      .pipe(
        map((result: SchadenUebersicht[]) => result.sort((a, b) => {
          if (a.schadenStatus === b.schadenStatus) {
            return new Date(b.meldeDatum).getTime() - new Date(a.meldeDatum).getTime();
          }
          const rankA = this.ORDER_BY_STATUS[a.schadenStatus.toLowerCase()] || 99;
          const rankB = this.ORDER_BY_STATUS[b.schadenStatus.toLowerCase()] || 99;

          return rankA - rankB;
        }))
      )
      .subscribe(
        result => {
          this.schadenUebersicht = result;
          this.schadenUebersichtIsLoading = false;
        },
        error => this.gsmErrorService.catchError(error)
      );

    entwurfUebersicht$.subscribe(
      result => {
        this.entwurfUebersicht = result;
        this.entwurfUebersichtIsLoading = false;
      },
      error => this.gsmErrorService.catchError(error)
    );
  }

  async handleSelect(schaden: SchadenUebersicht): Promise<void> {
    if (!schaden) {
      return;
    }

    const processKey = schaden.processInstanceId;

    if (schaden.checkListStatus === 'K') {
      if (schaden.gsmEnabled ||
        schaden.vertragsSparteId === '71' ||
        schaden.vertragsSparteId === '73' ||
        schaden.vertragsSparteId === '51' ||
        schaden.vertragsSparteId === '52'
      ) {
        await this.router.navigate([`/schaden/melden/${schaden.polizzeNummer}/${schaden.schadenDatum}`], {queryParams: {schadenNr: schaden.schadenNummer}});
      } else {
        window.location.href = `nvasp:briefe/${schaden.polizzeNummer}`;
      }
    } else {
      if (processKey) {
        this.llapi.patchValue('gsm.processInstanceId', processKey);
        await this.router.navigate([`/schaden/bearbeiten/${processKey}`]);
      } else if (schaden.schadenNummer) {
        if (schaden.checkListStatus && schaden.checkListStatus.toUpperCase() !== 'K') {
          await this.routerService.routeChecklistItemNachbringen(schaden.schadenNummer);
        } else {
          window.location.href = `nvic:show/schads/${schaden.schadenNummer}`;
        }
      }
    }
  }

  async schadenMelden() {
    this.llapi.patchValue('gsm.processInstanceId', null);
    await this.router.navigate(['/schaden/melden']);
  }

  toggleARGE(): void {
    this.isARGEorKBMA = !this.isARGEorKBMA;
    this.updateOverview();
  }

  deleteSchadenEvent(event: SchadenUebersicht) {
    if (event?.processInstanceId) {
      this.deleteSchaden(event.processInstanceId);
    }
  }

  deleteSchaden(processInstanceId: string) {
    if (processInstanceId) {
      this.isDeleteDisabled = true;
      this.gsmService.deleteProcess(processInstanceId)
        .subscribe(response => {
            if (response === true) {
              this.entwurfUebersicht = this.entwurfUebersicht.filter(schaden => schaden.processInstanceId !== processInstanceId);
              this.isDeleteDisabled = false;
              if (this.processInstanceId === processInstanceId) {
                this.llapi.patchValue('gsm.processInstanceId', null);
                this.schwebebestand = null;
              }
            }
          }, err => {
            this.gsmErrorService.catchError(err);
            this.isDeleteDisabled = false;
          }
        );
    }
  }

  async deleteAllEntwuerfe() {
    this.messageService.showSimpleOkCancelDialog(
        `Möchten Sie sich wirklich alle Entwürfe löschen?`, 'Alle Entwürfe löschen', 'LÖSCHEN', 'ABBRECHEN', 'yes', Action.STAY)
      .afterClosed()
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(this.handleDeleteDialogResult.bind(this));
  }

  private async handleDeleteDialogResult(dialogResult: ReactiveDialogResult<any>) {
    const kunde = {kundeId: this.personId ? this.personId.toString() : null} as Kunde;
    if (dialogResult?.status === 'yes') {
      this.entwurfUebersichtIsLoading = true;

      const deleteResponse$ = kunde?.kundeId
        ? this.gsmService.deleteAllKundeProcesses(kunde.kundeId)
        : this.gsmService.deleteAllBenutzerProcesses(this.benutzer.reporterEmail);
      const deleteResponse = await firstValueFrom(deleteResponse$);

      if (deleteResponse === true) {
        this.entwurfUebersicht = [];
        this.schwebebestand = null;
        // delete from localStorage
        if (localStorage.getItem('lastEditDraft')) {
          localStorage.removeItem('lastEditDraft');
        }
      }
      this.entwurfUebersichtIsLoading = false;
    }
  }

  printSchaden(event: SchadenUebersicht) {
    this.peziService.openPeziWithLogin(`/analyse?value=${event.schadenNummer}`, this.destroyRef);
  }

  showEsignDialog(event: SchadenUebersicht) {
    this.dialog.open(GsmEsignDialogComponent, {
      width:        '80vw',
      disableClose: true,
      data:         {
        schadenNummer: event.schadenNummer
      }
    });
  }

  toggleGsmNotification(event: SchadenUebersicht) {
    const schadenNummer = (event && event.schadenNummer) || '';

    if (schadenNummer) {
      this.gsmService
        .toggleGsmNotificationBySchaden(this.benutzer, schadenNummer)
        .pipe(
          // update schaden uebersicht
          tap(() => {
            this.schadenUebersicht = this.schadenUebersicht.map(schaden =>
              schaden.schadenNummer === schadenNummer
                ? {
                  ...schaden,
                  gsmNotificationEnabled: !schaden.gsmNotificationEnabled
                }
                : schaden
            );
          }))
        .subscribe({error: err => this.gsmErrorService.catchError(err)});
    }
  }

  private _checkDraftExpiry(lastSchadenEntwurf = {} as ProcessDraftInfo): void {
    if (lastSchadenEntwurf?.processInstanceId
      && (Date.now() - (+lastSchadenEntwurf.lastEditTimestamp || 0)) <= this.TIME_LIMIT_DRAFT) {
      this.gsmService.getSchadenEntwurfByProcessInstanceId(lastSchadenEntwurf.processInstanceId)
        .pipe(
          this.gsmErrorService.handleErrorAndReturnNull()
        )
        .subscribe({
          next:  schadenEntwurf => {
            if (!schadenEntwurf) {
              this.deleteSchaden(schadenEntwurf.processInstanceId);
              return;
            }
            this.schwebebestand = schadenEntwurf;
          },
          error: err => this.gsmErrorService.catchError(err)
        });
    } else {
      // * if there was a stored entry it was invalid, so we remove it.
      this.schwebebestand = null;
      this.clearProcessDraft();
    }
  }

  // TODO service for common gsm methods
  private clearProcessDraft() {
    this.llapi.resetValues([
      'gsm.processInstanceId',
      'gsm.lastEditTimestamp',
      'gsm.selectedTaskId',
      'gsm.currentFormInput',
      'gsm.currentHistoryFormInput'
    ]);
  }
}
